// zr-core/product — the two ZeroRate Connectra products + peer-role state machine. PURE and
// framework-agnostic: it validates the instantiation config and resolves a normalized
// ProductProfile (provider, caps, cap size, role) that the engine, demo, and codegen all agree on.
//
//   • connectra_basic  → "ZeroRate Connectra"  : Raw WebRTC P2P + polling, ≤10, WhatsApp-style VoIP.
//   • connectra_plus   → "ZeroRate Connectra+" : Localised WebRTC Media Manager + WHIP/WHEP for
//                        CXaaS digital contact centres. Adds a peer role — client | operator | admin —
//                        driving the call-queue (clients publish, operators consume, admins supervise).

import { MAX_MATCHMAKER_PARTICIPANTS, MAX_SFU_PARTICIPANTS, type SignalingProvider } from './index';

export type BillingMode = 'connectra_basic' | 'connectra_plus';
/** Connectra+ only. Clients call, operators answer the queue, admins supervise/escalate. */
export type PeerRole = 'client' | 'operator' | 'admin';
/** An operator/agent's live availability, surfaced in the presence directory. */
export type AvailabilityStatus = 'active' | 'busy' | 'offline';

export interface ProductCapabilities {
  /** Raw device↔device P2P media (Connectra Basic). */
  rawP2P: boolean;
  /** Managed WHIP/WHEP media infrastructure (Connectra+). */
  mediaManager: boolean;
  /** A call queue exists (client publishes → operator consumer group). */
  callQueue: boolean;
  /** This peer PUBLISHES calls into the queue (client). */
  isPublisher: boolean;
  /** This peer CONSUMES calls from the queue (operator/admin). */
  isConsumer: boolean;
  /** This peer can be pulled into an active call as an escalation (admin). */
  canEscalate: boolean;
  /** This peer can monitor operators (working hours, call rates, drops) (admin). */
  canMonitor: boolean;
  /** This peer can schedule a "call me back" request (client). */
  canScheduleCallBack: boolean;
}

export interface ProductProfile {
  billingMode: BillingMode;
  /** Human product name. */
  product: 'ZeroRate Connectra' | 'ZeroRate Connectra+';
  /** The Signaling Infrastructure Provider this product pins. */
  provider: SignalingProvider;
  /** Hard participant ceiling for the product. */
  maxParticipants: number;
  /** Connectra+ only. */
  peerRole?: PeerRole;
  capabilities: ProductCapabilities;
}

export interface ProductConfigInput {
  billingMode?: BillingMode;
  peerRole?: PeerRole;
  /** A developer-pinned provider; validated against the billing mode. */
  provider?: SignalingProvider;
}

const BASIC_CAPS: ProductCapabilities = {
  rawP2P: true,
  mediaManager: false,
  callQueue: false,
  isPublisher: false,
  isConsumer: false,
  canEscalate: false,
  canMonitor: false,
  canScheduleCallBack: false,
};

/**
 * Resolve the normalized product profile. `connectra_basic` pins the raw-P2P matchmaker (≤10);
 * `connectra_plus` pins the WHIP-WHEP media manager and layers the role capabilities. Defaults to
 * basic when unset (back-compat). Does NOT throw — call {@link validateProductConfig} to fail loud.
 */
export function resolveProductProfile(input: ProductConfigInput = {}): ProductProfile {
  const billingMode: BillingMode = input.billingMode ?? 'connectra_basic';

  if (billingMode === 'connectra_basic') {
    return {
      billingMode,
      product: 'ZeroRate Connectra',
      provider: 'serverless-matchmaker',
      maxParticipants: MAX_MATCHMAKER_PARTICIPANTS,
      capabilities: { ...BASIC_CAPS },
    };
  }

  const peerRole: PeerRole = input.peerRole ?? 'client';
  const isClient = peerRole === 'client';
  const isOperator = peerRole === 'operator';
  const isAdmin = peerRole === 'admin';
  return {
    billingMode,
    product: 'ZeroRate Connectra+',
    provider: 'whip-whep',
    maxParticipants: MAX_SFU_PARTICIPANTS,
    peerRole,
    capabilities: {
      rawP2P: false,
      mediaManager: true,
      callQueue: true,
      isPublisher: isClient,
      isConsumer: isOperator || isAdmin,
      canEscalate: isAdmin,
      canMonitor: isAdmin,
      canScheduleCallBack: isClient,
    },
  };
}

/**
 * Validate a product config and fail loud with actionable guidance. Enforces the product rules so a
 * misconfigured session can't silently pick the wrong plane (e.g. a contact-centre role on raw P2P).
 */
export function validateProductConfig(input: ProductConfigInput & { maxParticipants?: number }): void {
  const billingMode: BillingMode = input.billingMode ?? 'connectra_basic';

  if (billingMode === 'connectra_basic') {
    if (input.provider === 'whip-whep') {
      throw new Error('Connectra Basic is Raw P2P + polling only; do not pin provider "whip-whep". Use connectra_plus for the media manager.');
    }
    if (input.peerRole) {
      throw new Error('Connectra Basic has no peer roles; "peerRole" applies only to connectra_plus (CXaaS).');
    }
    if (input.maxParticipants != null && input.maxParticipants > MAX_MATCHMAKER_PARTICIPANTS) {
      throw new Error(`Connectra Basic (Raw P2P mesh) caps at ${MAX_MATCHMAKER_PARTICIPANTS} participants; requested ${input.maxParticipants}. Switch to connectra_plus.`);
    }
    return;
  }

  // connectra_plus
  if (input.provider === 'serverless-matchmaker') {
    throw new Error('Connectra+ uses the WHIP-WHEP media manager; do not pin provider "serverless-matchmaker".');
  }
  if (input.peerRole && !['client', 'operator', 'admin'].includes(input.peerRole)) {
    throw new Error(`Invalid peerRole "${input.peerRole}". Connectra+ roles are: client, operator, admin.`);
  }
}

/** Human label for a role, for UI + logs. */
export const PEER_ROLE_LABELS: Record<PeerRole, string> = {
  client: 'Client (caller)',
  operator: 'Operator (agent)',
  admin: 'Admin (supervisor)',
};
